import {
    isValidPhoneNumber,
    parsePhoneNumberWithError,
} from "../libphonenumber";
import {
    picklist,
    type InferOutput,
    custom,
    safeParse,
    pipe,
    string,
    email,
    object,
    optional,
    parse,
} from "valibot";

export const ContactIdentifierType = picklist(["email", "phone", "user"]);
export type ContactIdentifierType = InferOutput<typeof ContactIdentifierType>;

export const contactIdentifier = (message?: string) =>
    custom<ContactIdentifier>((value) => {
        if (typeof value !== "string") return false;

        const parts = value.split("::");
        if (parts.length < 2 || parts.length > 3) return false;

        const [type, identifier, meta] = parts;
        if (
            !ContactIdentifierType.options.includes(
                type as ContactIdentifierType
            )
        ) {
            return false;
        }

        // if (type === "user" && !meta) return false; // User type must have meta
        if (type !== "user" && meta) return false; // Non-user types should not have meta

        if (
            type === "email" &&
            !safeParse(pipe(string(), email()), identifier).success
        ) {
            return false;
        }

        if (type === "phone" && !isValidPhoneNumber(identifier)) return false;
        return true;
    }, message);
// The last section is currently for user to determine email or phone for the user
export type ContactIdentifier =
    | `${ContactIdentifierType}::${string}`
    | `${ContactIdentifierType}::${string}::${string}`;

export function serializeContactInfo(opts: ContactInfo): ContactIdentifier {
    const { contactType, inputType, value } = opts;

    if (inputType === "user") {
        return `user::${value}::${contactType}`;
    }

    if (contactType === "email") {
        try {
            const parsed = parse(pipe(string(), email()), value);
            return `email::${parsed}`;
        } catch {
            throw new Error(
                `[serializeContactInfo] Invalid email address: ${value}.`
            );
        }
    }

    if (contactType === "phone") {
        try {
            const number = parsePhoneNumberWithError(value, "CA");
            const parsed = number.format("E.164");
            return `phone::${parsed}`;
        } catch {
            throw new Error(
                `[serializeContactInfo] Invalid phone number: ${value}.`
            );
        }
    }

    throw new Error(`Invalid contact info: ${opts}`);
}

export function deserializeContactInfo(opts: ContactIdentifier): ContactInfo {
    const parts = opts.split("::");
    if (parts.length < 2 || parts.length > 3) {
        throw new Error(`Invalid contact identifier: ${opts}`);
    }

    const [type, value, meta] = parts;

    if (
        !ContactIdentifierType.options.includes(type as ContactIdentifierType)
    ) {
        throw new Error(`Invalid contact type: ${type}`);
    }

    if (type === "email" || type === "phone") {
        if (
            type === "email" &&
            !safeParse(pipe(string(), email()), value).success
        ) {
            throw new Error(`Invalid email address: ${value}`);
        }
        if (type === "phone" && !isValidPhoneNumber(value)) {
            throw new Error(`Invalid phone number: ${value}`);
        }

        return {
            contactType: type as "email" | "phone",
            value,
            inputType: "manual",
        };
    } else {
        return {
            contactType: meta as "email" | "phone",
            inputType: "user",
            value,
        };
    }
}

/**
 * @deprecated use contactInfo() instead.
 */
export const ContactInfo = object({
    contactType: picklist(["email", "phone"]),
    inputType: picklist(["manual", "user"]),
    value: string(),
});
export type ContactInfo = InferOutput<typeof ContactInfo>;

export const contactInfo = (message?: string) =>
    object(
        {
            ...ContactInfo.entries,
        },
        message
    );

export const ContactInfoWithPersonalInfo = object({
    ...ContactInfo.entries,
    userId: optional(string()),
    contactIdentifier: contactIdentifier(),
});
export type ContactInfoWithPersonalInfo = InferOutput<
    typeof ContactInfoWithPersonalInfo
>;
